import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Sparkles, X } from "lucide-react";
import type { Teammate } from "@/lib/types";

type Props = { teammate: Teammate };

const SAMPLE_INPUTS: Record<string, string> = {
  corporate_ma:
    'Clause from SPA draft (Counterparty A): "The Seller hereby represents and warrants that, except as disclosed in Schedule 4.7, there are no pending or threatened claims, actions or investigations against the Company or its subsidiaries that, individually or in the aggregate, would reasonably be expected to result in a Material Adverse Effect, with a cap on indemnification of EUR 2,000,000 and a survival period of 18 months from Closing." Standard or to push back?',
  litigation:
    'Excerpt — acórdão do Tribunal da Relação de Lisboa (commercial chamber): "Considera-se que a cláusula penal estabelecida no contrato é manifestamente excessiva quando o montante peticionado se mostra desproporcional ao prejuízo efetivamente sofrido pelo credor, devendo ser reduzida equitativamente nos termos do artigo 812.º do Código Civil." Resumo + implicação para o nosso caso (cliente devedor).',
  tax:
    'Fact pattern: Portuguese SA with EUR 12M revenue, 60% from EU exports. Considering setting up an IP holdco in Madeira to license its trademarks. Group has prior MAP procedure pending with NL. CFO asks: viable IRC efficiency, key CFC risks under article 66.º CIRC, and Pillar Two exposure given consolidated group of EUR 950M revenue?',
  labour:
    'Caso: dois trabalhadores em teletrabalho 100%, regime de adaptabilidade individual, querem rescindir invocando "violação culposa de direitos" por o empregador ter alterado unilateralmente o horário base. Justa causa? Indemnização aplicável? Referir CT artigos 394.º e 396.º.',
  real_estate:
    'Clause from a Portuguese commercial lease draft: "O Locatário renuncia expressamente ao direito de denúncia previsto no artigo 1110.º do CC e aceita que qualquer obra de adaptação realizada no locado fica a pertencer ao Locador findo o contrato, sem direito a indemnização." Enforceability and red flags for the tenant?',
  banking_finance:
    'Term sheet — secured facility agreement, EUR 50M revolver, Portuguese borrower, English law, LMA-based. Key terms: financial covenants (leverage ≤ 3.5x EBITDA tested quarterly, interest cover ≥ 4x), MAC clause defined broadly, change of control = mandatory prepayment, security package includes share pledge over a Madeira SGPS. Initial diligence flags?',
  ip:
    'Question: client (Portuguese SaaS startup) trained an LLM fine-tuning model on publicly available Portuguese court decisions (DGSI). Considering commercial release. Risks under text-and-data mining exception (article 4.º DSM Directive), sui generis database right (article 7.º Directive 96/9), and personal data in the decisions (RGPD)?',
  competition:
    'Joint venture between two Portuguese hospital groups (combined 23% national market share in private inpatient care). Considering full-function JV in MedTech procurement. Notifiable under Portuguese AdC turnover thresholds? EUMR vs national? Likely theory of harm — buyer power, foreclosure of independent hospitals?',
  tmt:
    'New product: AI-powered customer service bot trained on transcripts of past calls (with consent). Portuguese telco operator deploying it for B2C complaints. Key GDPR / EU AI Act questions: classification (high-risk Annex III? limited-risk?), DPIA scope, transparency duties under article 50.º AI Act, retention of training data?',
  public_law:
    'Concurso público para concessão de transporte rodoviário urbano. Cliente foi excluído por suposta inexistência de capacidade técnica (referência negativa anterior). Programa do concurso permite exclusão por "idoneidade comercial duvidosa". Impugnação administrativa ou contencioso pré-contratual? CCP artigos 70.º, 146.º.',
  restructuring:
    'Empresa portuguesa em situação económica difícil, EUR 18M de dívida bancária, EBITDA negativo, fornecedores estratégicos em rutura. Considerar PER vs PEAP vs insolvência com plano. Implicações para administradores (responsabilidade pelo agravamento da situação) e timing crítico para evitar suspeitas de favorecimento de credores.',
  compliance:
    'Internal whistleblowing report (anonymous, via channel): senior manager allegedly approved invoices from a supplier owned by his brother-in-law over 18 months, total EUR 320k, no procurement record. RGPD/Lei n.º 93/2021 implications for investigation, preservation of records, interviewing the manager, and whether to involve the Ministério Público.',
  other:
    'Memo request: client wants a 1-page note on whether GDPR-compliant cross-border data transfers to Brazil are possible under the LGPD adequacy decision-equivalent process. Include current state of Article 46 SCC alternatives if no adequacy yet.',
};

export function TryItNow({ teammate }: Props) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [running, setRunning] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const sample =
    (teammate.sampleInput && teammate.sampleInput.trim()) ||
    SAMPLE_INPUTS[teammate.function] ||
    SAMPLE_INPUTS.other;

  async function run() {
    if (!input.trim() || running) return;
    setRunning(true);
    setOutput("");
    const ac = new AbortController();
    abortRef.current = ac;

    try {
      const r = await fetch("/api/run", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ teammateId: teammate.id, input }),
        signal: ac.signal,
      });
      if (!r.ok || !r.body) {
        const text = await r.text();
        throw new Error(text.slice(0, 200));
      }
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setOutput((o) => o + dec.decode(value, { stream: true }));
      }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === "AbortError") return;
      setOutput((o) => o + `\n\n[error: ${e instanceof Error ? e.message : "run failed"}]`);
    } finally {
      setRunning(false);
    }
  }

  function stop() {
    abortRef.current?.abort();
    setRunning(false);
  }

  function loadSample() {
    setInput(sample);
  }

  return (
    <div className="glass glow-border mt-5 rounded-lg p-5">
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center rounded-xl"
          style={{ background: `${teammate.accent}22` }}
        >
          <Sparkles className="h-5 w-5 text-cyan-glow" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-cyan-glow">
            See it work
          </div>
          <div className="text-base font-medium text-white">
            Put {teammate.name} to work on a real input
          </div>
        </div>
        {!open && (
          <button className="btn-ghost" onClick={() => setOpen(true)}>
            Try now
          </button>
        )}
        {open && (
          <button
            onClick={() => {
              setOpen(false);
              setInput("");
              setOutput("");
              stop();
            }}
            className="text-white/50 hover:text-white"
            aria-label="close"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-4 flex items-center justify-between">
              <div className="text-xs text-white/50">Paste your real task input</div>
              {!input && (
                <button
                  className="text-xs text-cyan-glow hover:underline"
                  onClick={loadSample}
                >
                  use a sample
                </button>
              )}
            </div>
            <textarea
              className="input-glass mt-2 min-h-[110px] resize-none text-sm"
              placeholder={`e.g. "${sample.slice(0, 100)}…"`}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={4}
            />
            <div className="mt-2 flex justify-end">
              {running ? (
                <button className="btn-ghost" onClick={stop}>
                  Stop
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={run}
                  disabled={!input.trim()}
                >
                  Run {teammate.name} <Send className="h-4 w-4" />
                </button>
              )}
            </div>

            <AnimatePresence>
              {(output || running) && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mt-4 rounded-lg p-4"
                  style={{
                    background: `linear-gradient(180deg, ${teammate.accent}14, ${teammate.accent}06)`,
                    border: `1px solid ${teammate.accent}33`,
                  }}
                >
                  <div
                    className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest"
                    style={{ color: teammate.accent }}
                  >
                    {teammate.name} says
                    {running && (
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-glow/80" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-glow" />
                      </span>
                    )}
                  </div>
                  <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-white/90">
                    {output}
                    {running && <span className="animate-pulse text-cyan-glow">▍</span>}
                  </pre>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
